import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import formatPrice from "@/core/helpers/formatPrice";
import { RubleIcon } from "@/shared/icons";
import { Switcher } from "@/shared/ui";
import PersonalDataCard from "@/shared/widgets/PersonalDataCard";
import { EstateState } from "@/domains/estate/slice";

type Props = {
  tenant: EstateState["tenants"]["data"][0];
  onChange: (data: Partial<EstateState["tenants"]["data"][0]>) => void;
  onCall?: () => void;
  onMsg?: () => void;
};

export default function EstateTenantInfo({ tenant, onChange, onCall, onMsg }: Props) {
  const { t } = useTranslation("estate");

  const permissions: [string, boolean][] = [
    ["fridge", tenant.permissions.fridge],
    ["garage", tenant.permissions.garage],
    ["wardrobe", tenant.permissions.wardrobe],
    ["safe", tenant.permissions.safe],
    ["upgrade", tenant.permissions.upgrade],
  ];

  const change = (key: string, value: boolean) => {
    onChange({ permissions: { ...tenant.permissions, [key]: value } });
  };

  return (
    <motion.div
      initial={{ opacity: 0, translateX: "100%" }}
      animate={{ opacity: 1, translateX: "none" }}
      transition={{ duration: 0.2, ease: "easeOut" }}
    >
      <PersonalDataCard
        key={tenant.characterId}
        name={tenant.name}
        id={tenant.characterId}
        isOnline={!!tenant.playerMpId}
        onCall={onCall}
        onMsg={onMsg}
      />
      <div
        className={clsx(
          "flex items-center justify-between gap-2.5 px-2.5 h-[3.5rem] rounded-[0.75rem] bg-gray/5 mt-1.5",
        )}
      >
        <RubleIcon size={20} className="text-inverted shrink-0" />
        <div className="text-11 font-medium uppercase text-gray/50">
          {t("estate.tenant.paidFee")}
        </div>
        <div className="text-14 font-bold uppercase text-[#83FE96] ml-auto">
          {formatPrice(tenant.paidFee)} ₽
        </div>
      </div>
      <div className="text-12 font-bold text-white/70 uppercase mt-5">
        {t("estate.tenant.permission")}:
      </div>
      {permissions.map((item) => {
        const [key, value] = item;
        return (
          <div
            className="flex items-center justify-between gap-5 py-3.5 border-b border-[#191919] px-1"
            key={key}
          >
            <div className="text-15 font-bold text-white">{t(`estate.permissions.${key}`)}</div>
            <Switcher on={!!value} onChange={() => change(key, !value)} withBorder />
          </div>
        );
      })}
      {tenant.fee && (
        <div className="flex items-center justify-between gap-5 py-3.5 px-1 text-15 font-bold">
          <div className="text-white">{t("estate.tenant.expensesPerDay")}</div>
          <div className="text-[#FF4E4E]">{formatPrice(tenant.fee)} ₽</div>
        </div>
      )}
    </motion.div>
  );
}
